import imageCompression from 'browser-image-compression';

const MAX_WIDTH_OR_HEIGHT = 1920;
const INITIAL_QUALITY = 1;

/**
 * Compress an image file using browser-image-compression
 * @param file - The image file to compress
 * @param maxSizeMB - Maximum file size in MB (default: 2MB)
 * @param initialQuality - Initial JPEG quality (0-1, default: 1)
 * @returns Promise that resolves to the compressed File
 */
export const compressImage = async (
  file: File,
  maxSizeMB: number = 2,
  initialQuality: number = INITIAL_QUALITY
): Promise<File> => {
  const options = {
    maxSizeMB: maxSizeMB,
    maxWidthOrHeight: MAX_WIDTH_OR_HEIGHT,
    useWebWorker: false,
    fileType: 'image/jpeg',
    initialQuality: initialQuality,
    alwaysKeepResolution: false
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    // Return original file if compression fails
    throw error;
  }
};

/**
 * Compress an image file to a specific target size (in MB)
 * Will progressively reduce quality until target size is met
 * @param file - The image file to compress
 * @returns Promise that resolves to the compressed File
 */
export const compressImageToSize = async (
  file: File
): Promise<File> => {
  const targetSizeMB = 2;
  let quality = 0.9;
  let compressedFile: File = file;

  // Try compressing with decreasing quality until we meet the target size
  while (quality > 0.1) {
    try {
      compressedFile = await compressImage(file, targetSizeMB, quality);
      
      // Check if we've met the target size
      if (compressedFile.size <= targetSizeMB * 1024 * 1024) {
        return compressedFile;
      }
      
      // Reduce quality for next iteration
      quality -= 0.1;
    } catch (error) {
      console.error('Error compressing image:', error);
      // Return the best compression we achieved, or original file
      return compressedFile;
    }
  }

  return compressedFile;
};

